import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import useDidMountEffect from "../../../../utils/useDidMountEffect";
import useCheckPermission from "../../../../utils/checkPermission";
import {
  getVoucherCodesStart,
  createVoucherCodeStart,
  updateVoucherCodeStart,
  deleteVoucherCodeStart,
} from "../../../../store/redux-slices/voucherCodesSlice";

const useVoucherCodes = (isActive = false) => {
  const { loading, voucherCodes } = useSelector(
    (state) => state.voucherCodes
  );

  const codes = voucherCodes?.data || [];
  const pagination = voucherCodes?.pagination || {};
  const currentConversionRate = voucherCodes?.currentConversionRate || null;

  const isInitialRender = useDidMountEffect();
  const [limit, setLimit] = useState(15);
  const [page, setPage] = useState(1);
  const [type, setType] = useState("");
  const [data, setData] = useState({});
  const [show, setShow] = useState(false);
  const [validation, setValidation] = useState({});
  const [selectedpromoCode, setSelectedpromocode] = useState(null);
  const [deletemodal, setDeleteModal] = useState(false);
  const { isHidden } = useCheckPermission();
  const totalPages = pagination?.totalPages;
  const totalCount = pagination?.totalItems;
  const dispatch = useDispatch();

  // Track if data has been fetched at least once
  const hasFetched = useRef(false);

  // Fetch when tab becomes active (first time or when limit changes)
  useEffect(() => {
    if (isActive) {
      setPage(1);
      dispatch(
        getVoucherCodesStart({
          limit: limit,
          pageNo: 1,
        })
      );
      hasFetched.current = true;
    }
  }, [isActive, limit]);

  // Fetch when page changes (only if tab is active and not initial render)
  useEffect(() => {
    if (isActive && !isInitialRender && hasFetched.current) {
      dispatch(
        getVoucherCodesStart({
          limit: limit,
          pageNo: page,
        })
      );
    }
  }, [page]);

  const fetchVoucherCodes = () => {
    dispatch(
      getVoucherCodesStart({
        limit: limit,
        pageNo: page,
      })
    );
  };

  const handleCreateEdit = (actionType, editData) => {
    setType(actionType);
    setData(editData);
    setShow(true);
  };

  const createUpdate = (formData) => {
    // Form amount is in TTD for vouchers; convert to USD for API
    const rate = parseFloat(currentConversionRate?.rate || "0");
    const amountTtd = parseFloat(formData.amount);
    const amountUsd = rate > 0 ? amountTtd / rate : 0;

    if (type === "Create") {
      // Map form data to API expected format for create
      const apiData = {
        amountUsd,
        expiresAt: formData.validity ? new Date(formData.validity).toISOString() : null,
      };

      // Map user-chosen status (boolean) to API expected string
      if (typeof formData.status !== "undefined") {
        apiData.status =
          typeof formData.status === "boolean"
            ? formData.status
              ? "active"
              : "inactive"
            : formData.status;
      }

      // Only add code if provided
      if (formData.code && formData.code.trim() !== "") {
        apiData.code = formData.code.toUpperCase();
      }

      // Only add notes if provided
      if (formData.notes && formData.notes.trim() !== "") {
        apiData.notes = formData.notes;
      }

      dispatch(
        createVoucherCodeStart({
          data: apiData,
          onSuccess: () => {
            setShow(false);
            fetchVoucherCodes();
          },
        })
      );
    } else {
      // Edit/Update - use voucherCodeId; always send status and isRedeemed
      const apiData = {
        voucherCodeId: formData.voucherCodeId || formData.promocodesId,
      };

      // Only add fields that have changed or are provided (form amount is TTD)
      if (formData.amount !== undefined && formData.amount !== "") {
        apiData.amountUsd = amountUsd;
      }

      if (formData.validity) {
        apiData.expiresAt = new Date(formData.validity).toISOString();
      }

      if (formData.code && formData.code.trim() !== "") {
        apiData.code = formData.code.toUpperCase();
      }

      if (formData.notes !== undefined) {
        apiData.notes = formData.notes;
      }

      // Always send status (changed or current)
      if (formData.status !== undefined) {
        apiData.status = typeof formData.status === "boolean"
          ? (formData.status ? "active" : "inactive")
          : formData.status;
      }

      // Always send isRedeemed (current from row when editing via modal)
      const currentIsRedeemed = data?.isRedeemed;
      if (currentIsRedeemed !== undefined) {
        apiData.isRedeemed = !!currentIsRedeemed;
      }

      dispatch(
        updateVoucherCodeStart({
          data: apiData,
          optimisticUpdate: false,
          onSuccess: () => {
            setShow(false);
            fetchVoucherCodes();
          },
        })
      );
    }
  };

  const onHandleDeleteConfirmation = (rowData) => {
    setSelectedpromocode(rowData);
    setDeleteModal(true);
  };

  const handleDelete = () => {
    const voucherCodeId = selectedpromoCode?.voucherCodeId ?? selectedpromoCode?.promocodesId;
    if (!voucherCodeId) return;
    dispatch(
      deleteVoucherCodeStart({
        voucherCodeId,
        onSuccess: () => {
          fetchVoucherCodes();
          setDeleteModal(false);
          setSelectedpromocode(null);
        },
      })
    );
  };

  const handleToggleStatus = (voucherCodeId, newStatus) => {
    const currentCode = codes.find(code => code.voucherCodeId === voucherCodeId);

    if (currentCode) {
      const statusString = newStatus ? "active" : "inactive";
      const currentIsRedeemed = !!currentCode.isRedeemed;

      dispatch(updateVoucherCodeStart({
        data: {
          voucherCodeId,
          status: statusString,
          isRedeemed: currentIsRedeemed,
        },
        optimisticUpdate: true,
        previousStatus: currentCode.status,
        onSuccess: () => { },
      }));
    } else {
      console.error('Voucher code not found with ID:', voucherCodeId);
    }
  };

  const handleToggleRedeemed = (voucherCodeId, newIsRedeemed) => {
    const currentCode = codes.find(code => code.voucherCodeId === voucherCodeId);

    if (currentCode) {
      const currentStatus = currentCode.status === "active" || currentCode.status === true
        ? "active"
        : "inactive";

      dispatch(updateVoucherCodeStart({
        data: {
          voucherCodeId,
          status: currentStatus,
          isRedeemed: !!newIsRedeemed,
        },
        optimisticUpdate: true,
        previousIsRedeemed: currentCode.isRedeemed,
        onSuccess: () => { },
      }));
    } else {
      console.error('Voucher code not found with ID (isRedeemed toggle):', voucherCodeId);
    }
  };

  return {
    loading,
    codes,
    currentConversionRate,
    isHidden,
    page,
    limit,
    setPage,
    setLimit,
    totalPages,
    fetchVoucherCodes,
    // Create/Edit related
    type,
    data,
    show,
    setShow,
    validation,
    setValidation,
    handleCreateEdit,
    createUpdate,
    handleToggleStatus,
    handleToggleRedeemed,
    // Delete related
    handleDelete,
    onHandleDeleteConfirmation,
    selectedpromoCode,
    setSelectedpromocode,
    deletemodal,
    setDeleteModal,
    dispatch,
    totalCount
  };
};

export default useVoucherCodes;
